import type { ReactNode } from 'react';
import { KV, KVRow } from '../components/KV';
import type { AppRenderer, AppPayload } from './types';

const SECRET_PATTERN =
  /(_token|_secret|_key|_hash|_credentials)$|^password|^access_token$|^refresh_token$/i;

function format_value(value: unknown): ReactNode {
  if (value === null || value === undefined)
    return <span className="muted">—</span>;
  if (typeof value === 'boolean') return <code>{String(value)}</code>;
  if (typeof value === 'number') return <code>{value.toString()}</code>;
  if (typeof value === 'string') {
    // ISO 8601 date heuristic
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    }
    return value;
  }
  if (Array.isArray(value) || typeof value === 'object') {
    return (
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          margin: 0,
          fontSize: 12,
          background: 'var(--green-50)',
          padding: 8,
          borderRadius: 4,
        }}
      >
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return String(value);
}

function render_fields(data: Record<string, unknown>): ReactNode {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <p className="muted">(no fields)</p>;
  }
  return (
    <KV>
      {entries.map(([key, value]) => {
        const is_secret = SECRET_PATTERN.test(key);
        return (
          <KVRow
            key={key}
            label={key}
            value={
              is_secret ? (
                <span className="muted">[redacted]</span>
              ) : (
                format_value(value)
              )
            }
          />
        );
      })}
    </KV>
  );
}

export const DefaultRenderer: AppRenderer = {
  app_name: '*',
  render(payload: AppPayload) {
    const subcol_entries = Object.entries(payload.subcollections);
    return (
      <div>
        <h3 style={{ marginTop: 0 }}>Fields</h3>
        {render_fields(payload.data)}
        {subcol_entries.length > 0 && (
          <>
            <h3 style={{ marginTop: 28 }}>Subcollections</h3>
            {subcol_entries.map(([name, docs]) => (
              <div key={name} style={{ marginBottom: 16 }}>
                <h4 style={{ margin: '12px 0 8px' }}>
                  {name}{' '}
                  <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>
                    ({docs.length} {docs.length === 1 ? 'doc' : 'docs'})
                  </span>
                </h4>
                {docs.map((doc) => (
                  <details
                    key={doc.id}
                    style={{
                      marginBottom: 8,
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      padding: '8px 12px',
                    }}
                  >
                    <summary style={{ cursor: 'pointer', fontSize: 13 }}>
                      <code>{doc.id}</code>
                    </summary>
                    <div style={{ marginTop: 12 }}>{render_fields(doc.data)}</div>
                  </details>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    );
  },
};
